// Native motion, isolated to the hero; static artwork remains usable without JS.
(() => {
  if (customElements.get('solvia-orbit-hero')) return;
  class SolviaOrbitHero extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas?.getContext('2d');
      this.visual = this.querySelector('.solvia-orbit__visual');
      this.button = this.querySelector('.solvia-orbit__motion');
      if (!this.context || !this.visual || !('IntersectionObserver' in window) || !('ResizeObserver' in window)) return;
      this.frame = 0;
      this.time = 0;
      this.previous = 0;
      this.x = this.y = this.targetX = this.targetY = 0;
      this.paused = false;
      this.visible = false;
      this.entrances = [];
      this.particles = Array.from({ length: 64 }, (_, i) => ({
        x: ((i * .6180339887) % 1), y: ((i * .4142135623) % 1),
        size: .45 + (i % 5) * .22, speed: .5 + (i % 7) * .13,
      }));
      const options = { signal: this.events.signal, passive: true };
      const sync = () => this.sync();
      this.reduced.addEventListener('change', sync, options);
      document.addEventListener('visibilitychange', sync, options);
      this.addEventListener('pointermove', (event) => {
        if (!this.running || !this.pointer.matches || event.pointerType === 'touch') return;
        const rect = this.getBoundingClientRect();
        this.targetX = (event.clientX - rect.left) / rect.width * 2 - 1;
        this.targetY = (event.clientY - rect.top) / rect.height * 2 - 1;
      }, options);
      const resetPointer = () => { this.targetX = this.targetY = 0; };
      this.addEventListener('pointerleave', resetPointer, options);
      this.addEventListener('pointercancel', resetPointer, options);
      this.pointer.addEventListener('change', resetPointer, options);
      this.button?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.button.setAttribute('aria-pressed', String(this.paused));
        this.button.querySelector('.solvia-orbit__motion-label').textContent = this.paused ? 'Resume animation' : 'Pause animation';
        this.button.querySelector('.solvia-orbit__motion-icon').textContent = this.paused ? '▷' : 'Ⅱ';
        this.sync();
      }, { signal: this.events.signal });
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this);
      this.resize.observe(this.visual);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      }, { threshold: 0 });
      this.observer.observe(this);
      this.dataset.enhanced = '';
      this.measure();
      this.sync();
    }

    measure() {
      const bounds = this.getBoundingClientRect();
      // Layout coordinates stay stable during the visual's entrance transform.
      const art = this.visual;
      const parent = art.offsetParent;
      this.width = bounds.width;
      this.height = bounds.height;
      this.cx = parent.offsetLeft + art.offsetLeft + art.offsetWidth * .48;
      this.cy = parent.offsetTop + art.offsetTop + art.offsetHeight * .49;
      this.radius = art.offsetWidth * .44;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      this.canvas.width = Math.round(this.width * dpr);
      this.canvas.height = Math.round(this.height * dpr);
      this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.draw();
    }

    sync() {
      const allowed = !this.reduced.matches && !window.Shopify?.designMode;
      if (this.button) this.button.hidden = !allowed;
      this.running = allowed && this.visible && !document.hidden && !this.paused;
      this.dataset.running = String(this.running);
      this.entrances.forEach((animation) => {
        if (!allowed || this.paused) animation.cancel();
        else if (this.running) animation.play();
        else animation.pause();
      });
      if (!allowed) {
        this.x = this.y = this.targetX = this.targetY = 0;
        this.style.setProperty('--hero-x', 0);
        this.style.setProperty('--hero-y', 0);
        this.style.setProperty('--hero-scroll', 0);
      }
      if (this.running) {
        this.enter();
        if (!this.frame) this.frame = requestAnimationFrame((time) => this.tick(time));
      } else {
        cancelAnimationFrame(this.frame);
        this.frame = this.previous = 0;
      }
    }

    enter() {
      if (this.entered || !this.animate) return;
      this.entered = true;
      const ease = 'cubic-bezier(.16, 1, .3, 1)';
      [this.querySelector('.solvia-orbit__heading'), this.querySelector('.solvia-orbit__content')].forEach((element, i) => {
        this.entrances.push(element.animate([
          { opacity: 0, transform: 'translateY(32px)', filter: 'blur(8px)' },
          { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
        ], { duration: 1300, delay: i * 160, easing: ease, fill: 'backwards' }));
      });
      this.entrances.push(this.visual.animate([
        { opacity: 0, transform: 'translate(65px, 30px) scale(.84)', filter: 'blur(12px)' },
        { opacity: 1, transform: 'translate(0, 0) scale(1)', filter: 'blur(0)' },
      ], { duration: 1900, delay: 120, easing: ease, fill: 'backwards' }));
      this.entrances.forEach((animation) => animation.addEventListener('finish', () => {
        this.entrances = this.entrances.filter((item) => item !== animation);
      }, { once: true }));
    }

    tick(time) {
      this.frame = 0;
      if (!this.running) return;
      const elapsed = this.previous ? time - this.previous : 34;
      // Canvas is capped at 30 fps; the product's CSS float remains compositor driven.
      if (elapsed >= 32) {
        const dt = Math.min(elapsed, 70);
        this.previous = time;
        this.time += dt / 1000;
        const ease = 1 - Math.exp(-dt / 150);
        this.x += (this.targetX - this.x) * ease;
        this.y += (this.targetY - this.y) * ease;
        this.style.setProperty('--hero-x', this.x.toFixed(4));
        this.style.setProperty('--hero-y', this.y.toFixed(4));
        const scroll = Math.min(1, Math.max(0, -this.getBoundingClientRect().top / this.height));
        this.style.setProperty('--hero-scroll', scroll.toFixed(4));
        this.draw();
      }
      this.frame = requestAnimationFrame((next) => this.tick(next));
    }

    point(angle, ring) {
      const radius = this.radius * (1 + ring * .075);
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius * (.39 + ring * .055);
      const tilt = -.42 + ring * .19 + this.x * .035;
      return {
        x: this.cx + x * Math.cos(tilt) - y * Math.sin(tilt) + this.x * 12,
        y: this.cy + x * Math.sin(tilt) + y * Math.cos(tilt) + this.y * 8,
      };
    }

    draw() {
      const ctx = this.context;
      if (!this.width || !this.height) return;
      ctx.clearRect(0, 0, this.width, this.height);
      // Quiet stardust in the product half of the composition.
      this.particles.forEach((particle, i) => {
        const x = (particle.x * this.width + this.time * particle.speed * 3) % this.width;
        const y = (particle.y * this.height - this.time * particle.speed * 2 + this.height) % this.height;
        const fade = this.width < 750 ? .35 : .16 + .45 * x / this.width;
        ctx.fillStyle = `rgba(224,199,153,${fade * (.5 + .5 * Math.sin(i + this.time * .4) ** 2)})`;
        ctx.beginPath(); ctx.arc(x, y, particle.size, 0, Math.PI * 2); ctx.fill();
      });
      for (let ring = 0; ring < 5; ring++) {
        ctx.beginPath();
        for (let step = 0; step <= 120; step++) {
          const point = this.point(step / 120 * Math.PI * 2, ring);
          if (!step) ctx.moveTo(point.x, point.y); else ctx.lineTo(point.x, point.y);
        }
        ctx.strokeStyle = `rgba(213,177,115,${.055 + ring * .008})`;
        ctx.lineWidth = .65;
        ctx.stroke();
        // One luminous comet travels each orbit, with a tapered fading tail.
        const head = this.time * (.16 + ring * .035) + ring * 1.9;
        for (let tail = 0; tail < 28; tail++) {
          const start = this.point(head - tail * .018, ring);
          const end = this.point(head - (tail + 1) * .018, ring);
          ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y);
          ctx.strokeStyle = `rgba(241,205,143,${(1 - tail / 28) * .6})`;
          ctx.lineWidth = (1 - tail / 28) * 1.6;
          ctx.stroke();
        }
        const point = this.point(head, ring);
        const glow = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, 11);
        glow.addColorStop(0, '#ffe6b89c'); glow.addColorStop(.22, '#e4b76d35'); glow.addColorStop(1, '#e4b76d00');
        ctx.fillStyle = glow; ctx.fillRect(point.x - 11, point.y - 11, 22, 22);
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.entrances?.forEach((animation) => animation.cancel());
      cancelAnimationFrame(this.frame);
      this.events = null;
    }
  }
  customElements.define('solvia-orbit-hero', SolviaOrbitHero);
})();
